import { useCallback, useEffect, useState } from "react";
import { Check, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageTitle from "../components/PageTitle";
import SwipeableRow from "../components/SwipeableRow";
import { deleteFavorite, loadFavorites, type Favorite } from "../lib/favorites";
import { saveMealRemote } from "../lib/storage";
import { MEAL_TYPE_ICONS, MEAL_TYPE_LABELS, MEAL_TYPES, type MealType } from "../lib/types";

export default function Favorites() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<{ id: string; mealType: MealType } | null>(null);

  const refresh = useCallback(async () => {
    try {
      setFavorites(await loadFavorites());
      setError(null);
    } catch (e) {
      setError((e as Error)?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!savedAt) return;
    const t = setTimeout(() => setSavedAt(null), 1800);
    return () => clearTimeout(t);
  }, [savedAt]);

  async function logToMealType(fav: Favorite, mealType: MealType) {
    setSavingId(fav.id);
    try {
      await saveMealRemote({
        meal_type: mealType,
        estimate: fav.estimate,
        caption: fav.caption,
        source_providers: fav.source_providers,
        externalPhotoUrl: fav.external_photo_url
      });
      setSavedAt({ id: fav.id, mealType });
    } catch (e) {
      setError((e as Error)?.message ?? String(e));
    } finally {
      setSavingId(null);
    }
  }

  async function onDelete(id: string) {
    try {
      await deleteFavorite(id);
      await refresh();
    } catch (e) {
      setError((e as Error)?.message ?? String(e));
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="text-sm text-slate-400">← Back</button>
          <PageTitle>Favorites</PageTitle>
        </div>
        <button
          onClick={() => navigate("/favorites/new")}
          className="px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-950 text-sm font-semibold"
        >
          + New
        </button>
      </div>

      <p className="text-xs text-slate-500">
        Save any meal as a favorite (★ on the meal in the Today view), or tap <span className="text-emerald-400">+ New</span> to build a custom recipe from ingredients.
      </p>

      {error && (
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="text-center text-slate-500 py-8 text-sm">Loading…</div>
      ) : favorites.length === 0 ? (
        <div className="text-center py-10 space-y-3">
          <Star className="w-12 h-12 text-amber-400 mx-auto" strokeWidth={1.5} />
          <p className="text-sm text-slate-400 max-w-xs mx-auto leading-relaxed">
            Favorites are saved meals or custom recipes you can re-log in one tap. Tap the star icon on any meal in History, or build one from ingredients.
          </p>
          <button
            type="button"
            onClick={() => navigate("/favorites/new")}
            className="px-4 py-2.5 rounded-lg bg-emerald-500 text-slate-950 text-sm font-semibold"
          >
            + Build your first favorite
          </button>
        </div>
      ) : (
        <ul className="space-y-3">
          {favorites.map((f) => (
            <li key={f.id} className="rounded-2xl overflow-hidden border border-slate-800">
              <SwipeableRow onDelete={() => onDelete(f.id)} bgClass="bg-slate-900">
                <div className="p-3 space-y-3">
                  <div className="flex gap-3 items-start">
                    {f.photo_data_url ? (
                      <img src={f.photo_data_url} alt="" className="w-12 h-12 rounded-lg object-cover bg-slate-800 flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center text-2xl flex-shrink-0">
                        {f.meal_type ? MEAL_TYPE_ICONS[f.meal_type] : "⭐"}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-100 truncate">{f.name}</div>
                      <div className="text-xs text-slate-400">
                        {Math.round(f.estimate.totals.calories)} kcal ·
                        <span className="text-sky-400"> Protein {f.estimate.totals.protein_g.toFixed(0)}g</span> ·
                        <span className="text-amber-400"> Carbs {f.estimate.totals.carbs_g.toFixed(0)}g</span> ·
                        <span className="text-violet-400"> Fat {f.estimate.totals.fat_g.toFixed(0)}g</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-1">
                    {MEAL_TYPES.map((t) => {
                      const justSaved = savedAt?.id === f.id && savedAt?.mealType === t;
                      return (
                        <button
                          key={t}
                          onClick={(e) => {
                            e.stopPropagation();
                            logToMealType(f, t);
                          }}
                          disabled={savingId === f.id}
                          className={`py-2 rounded text-xs font-medium ${
                            justSaved
                              ? "bg-emerald-500 text-slate-950"
                              : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                          } disabled:opacity-50`}
                        >
                          {justSaved ? <Check className="w-3.5 h-3.5 inline" strokeWidth={2.5} /> : `${MEAL_TYPE_ICONS[t]} ${MEAL_TYPE_LABELS[t]}`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </SwipeableRow>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
